import React, { useEffect, useRef } from 'react';
import { Tooltip } from 'react-tooltip';

import { ReactComponent as QuestionMark } from '../../../images/question-icon.svg';
import RichTextConverter from '../RichText/RichTextConverter';
import { formatNumberAsCurrency, formatNumberWithCommas } from './helpers';
import * as styles from './Input.module.scss';
import * as tooltipStyles from './TooltipStyles.module.scss';

export default function Input({
  metric,
  properties,
  section,
  handleFocus,
  handleInput,
  handleBlur,
  handleSlider,
  index,
  focused,
}) {
  const ref = useRef();
  const { name } = metric;
  const fixedProperties = properties[name];
  const type = fixedProperties.type;
  const showAsNegative = name === 'conversionImpact' && metric.original !== '0.0';
  let tooltip;
  if (section.content) tooltip = section.content.find((item) => item.subtitle === name)?.body;
  if (section.fieldLabelTooltips) tooltip = section.fieldLabelTooltips.find((item) => item.subtitle === name)?.body;

  // Autofocus on field to draw attention
  useEffect(() => {
    if (focused && ref.current) ref.current.focus();
  }, [focused, ref]);

  let originalValue, newValue, maxLength;
  switch (type) {
    case 'number':
      originalValue = new Intl.NumberFormat().format(metric.original);
      newValue = new Intl.NumberFormat().format(Math.round(metric.new));
      maxLength = '11';
      break;
    case 'percentage':
      originalValue = showAsNegative ? `-${metric.original}` : metric.original;
      newValue = showAsNegative ? `-${metric.new.toFixed(1)}%` : `${metric.new.toFixed(1)}%`;
      maxLength = showAsNegative ? '5' : '4';
      break;
    case 'currency':
      originalValue = `$${formatNumberWithCommas(metric.original.toString())}`;
      newValue = formatNumberAsCurrency(metric.new, true);
      maxLength = '7';
      break;
    default:
      break;
  }

  return (
    <div
      className={[styles.sliderGroup, fixedProperties.color].join(' ')}
      data-analytics-component="roi-calculator-input"
      data-analytics-id={name}
    >
      <div className={styles.originalLabel} data-tooltip-id={`tt-${index}-${name}`}>
        <label htmlFor={`${name}-original`}>{fixedProperties.label}</label>
        {tooltip && <QuestionMark className={[tooltipStyles.tooltipIcon, `${name}-tooltip-icon`].join(' ')} />}
      </div>
      {tooltip && (
        <Tooltip
          id={`tt-${index}-${name}`}
          anchorSelect={`.${name}-tooltip-icon`}
          className={tooltipStyles.tooltip}
          classNameArrow={tooltipStyles.tooltipArrow}
          content={<RichTextConverter copy={tooltip} />}
        />
      )}
      <div className={[type === 'percentage' ? styles.percentage : '', styles.inputContainer].join(' ')}>
        <input
          ref={ref}
          type="text"
          inputMode="decimal"
          id={`${name}-original`}
          name={`${name}-original`}
          maxLength={maxLength}
          className={styles.originalValue}
          value={originalValue}
          onFocus={handleFocus}
          onChange={(e) => handleInput(e, name, type)}
          onBlur={(e) => handleBlur(e, name, type)}
        />
      </div>
      <div className={styles.sliderContainer}>
        <input
          type="range"
          id={`${name}-new`}
          name={`${name}-new`}
          className={['calculator-slider', styles.slider].join(' ')}
          min={0}
          max={4}
          step="1"
          value={metric.step}
          onChange={(e) => handleSlider(e, name)}
        />
        <span style={{ left: 'calc(0% + 0.58rem)' }} />
        <span style={{ left: 'calc(25% + 0.28rem)' }} />
        <span style={{ left: 'calc(50% - 0.02rem)' }} />
        <span style={{ left: 'calc(75% - 0.35rem)' }} />
        <span style={{ left: 'calc(100% - 0.65rem)' }} />
      </div>
      {index === 0 && <span className={styles.changedLabel}>New Value</span>}
      <div className={styles.changedValue}>{newValue}</div>
    </div>
  );
}
